import { useEffect, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import {
    Button,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";

import { ORGANIZATION_FUNCTION_ERROR_CODES } from "src/modules/data-protection/constants/organizationFunctions";

import useCreateOrganizationFunction from "./hooks/useCreateOrganizationFunction";

const CreateFunctionModal = ({ t, isOpen, toggle }) => {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [titleError, setTitleError] = useState(false);

    const createMutation = useCreateOrganizationFunction({
        t,
        onSuccess: toggle,
    });

    useEffect(() => {
        if (!isOpen) {
            setTitle("");
            setDescription("");
            setTitleError(false);
        }
    }, [isOpen]);

    const handleSubmit = () => {
        const trimmedTitle = (title || "").trim();

        if (!trimmedTitle) {
            setTitleError(true);
            toast(t(ORGANIZATION_FUNCTION_ERROR_CODES.TITLE_REQUIRED), {
                type: "error",
            });
            return;
        }

        setTitleError(false);

        const payload = {
            title: trimmedTitle,
        };

        if ((description || "").trim()) {
            payload.description = description.trim();
        }

        createMutation.mutate(payload);
    };

    return (
        <Modal isOpen={isOpen} backdrop="static">
            <ModalHeader toggle={toggle}>{t("Create Function")}</ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label className="form-label" htmlFor="create-function-title">
                        {`${t("Title")}:`} <span className="text-danger">*</span>
                    </Label>
                    <Input
                        id="create-function-title"
                        name="title"
                        type="text"
                        className="form-control"
                        value={title}
                        invalid={titleError}
                        disabled={createMutation.isLoading}
                        onChange={(event) => {
                            setTitle(event.target.value);
                            if (titleError && event.target.value.trim()) {
                                setTitleError(false);
                            }
                        }}
                    />
                    {titleError ? (
                        <div className="invalid-feedback d-block">
                            {t("This field cannot be blank")}
                        </div>
                    ) : null}
                </FormGroup>
                <FormGroup className="mb-0">
                    <Label
                        className="form-label"
                        htmlFor="create-function-description"
                    >
                        {`${t("Description")}:`}
                    </Label>
                    <Input
                        id="create-function-description"
                        name="description"
                        type="textarea"
                        rows={4}
                        className="form-control"
                        value={description}
                        disabled={createMutation.isLoading}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    type="button"
                    disabled={createMutation.isLoading}
                    onClick={toggle}
                >
                    {t("Cancel")}
                </Button>
                <Button
                    color="primary"
                    type="button"
                    disabled={createMutation.isLoading}
                    onClick={handleSubmit}
                >
                    {createMutation.isLoading ? (
                        <Spinner className="me-2" size="sm" />
                    ) : null}
                    {t("Create Function")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default withNamespaces()(CreateFunctionModal);
